"use client";

import { c as _c } from "react/compiler-runtime";
import CreateNoteWrapper from "../scribe/create-note-wrapper";
import { ScrollShadow } from "../ui/scroll-shadow";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaRegAddressCard, FaRegCalendarAlt, FaRegCreditCard, FaRegFileAlt, FaRegFilePdf, FaRegImages, FaRegMoneyBillAlt, FaRegStickyNote } from "react-icons/fa";
import { FaHeartbeat, FaSyringe, FaUsers } from "react-icons/fa";
import { FaRegNoteSticky } from "react-icons/fa6";
import { FiClipboard, FiPackage, FiUsers } from "react-icons/fi";
import { RiCloseLine } from "react-icons/ri";
import { TbTestPipe } from "react-icons/tb";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import AddPaymentMethodModal from "@/components/add-payment-method-modal";
import AppointmentCancellationModal from "@/components/appointment-cancellation-modal";
import AppointmentNoShowConfirmationModal from "@/components/appointment-no-show-confirmation-modal";
import { generateAppointmentCancellationMessage, getEventCancellationTemplate } from "@/components/custom-calendar/utils";
import AddDoseWrapper from "@/components/dose/add-dose-wrapper";
import SendFormsDialog from "@/components/forms/send-forms-dialog";
import ClickablePhoneNumber from "@/components/patient/call-modal/clickable-phone-number";
import ClickableEmail from "@/components/patient/clickable-email";
import DocumentUpload from "@/components/patient/documents/document-upload";
import HiddenContact from "@/components/patient/hidden-contact";
import { PatientCreditsDialog } from "@/components/patient/patient-credits-dialog";
import PatientDisplay from "@/components/patient/patient-display";
import { PatientIdentifier } from "@/components/patient/patient-identifier";
import ProfilePictureUpload from "@/components/patient/profile-picture-upload";
import { ProgressBarLink, useProgressNavigation } from "@/components/progress-bar";
import { SetupSidebarPanel } from "@/components/right-sidebar/setup-checklist/setup-sidebar-panel";
import { SidebarHeader, type SidebarHeaderTab } from "@/components/right-sidebar/sidebar-header";
import { TaskSidebarPanel } from "@/components/right-sidebar/task-sidebar-panel";
import { TrainingSidebarPanel } from "@/components/training/training-sidebar-panel";
import { Button, Divider, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle, Tooltip } from "@/components/ui";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import Badge from "@/components/ui/badge";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IdDisplay from "@/components/ui/id-display";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CancellationWaitlistDialog from "@/components/waitlist/cancellation-waitlist-dialog";
import { queryClient } from "@/config/react-query";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useHasAnyLabVendor } from "@/hooks/use-lab-vendor-modules";
import { useLocations } from "@/hooks/use-locations";
import { useModule } from "@/hooks/use-module";
import { useProviders } from "@/hooks/use-providers";
import { useSidebarEnvironment } from "@/hooks/use-sidebar-environment";
import { useSidebarRegistration } from "@/hooks/use-sidebar-registration";
import { useTaskSidebarPatientSync } from "@/hooks/use-task-sidebar-patient-sync";
import { useLocationTimezoneOf } from "@/hooks/use-timezone";
import { getEventNoteTemplateId, getEventNoteTemplateIds } from "@/lib/event-note-template";
import { calculateAge, formatAddress, formatCurrency, formatDate, formatName, formatStaffName, formatToHumanReadable, showsEmail, showsPhoneNumber } from "@/lib/format-data";
import { formatChange, formatMeasurementValue, getTrendColor } from "@/lib/measurement-calculations";
import { getFamilyHistory, getSocialHistory } from "@/lib/patient-health-fields";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { showsPatientIdentificationCard } from "@/lib/tenant-flags";
import { toMs } from "@/lib/timestamp";
import { EventStatus } from "@/types/enums/event";
import { RecurrenceModificationMode } from "@/types/enums/recurrence-modification-mode";
import { EventDetail } from "@/types/event";
import { MeasurementField, MeasurementValueSummary } from "@/types/measurement";
import { PatientChart } from "@/types/patient";
import type { SidebarEntry } from "@/types/sidebar-session";
import { appointmentChecklistSkeleton } from "./patient/appointment-checklist";
import { shouldShowSidebarChecklist } from "./patient/appointment-checklist-visibility";
import MedicalChart from "./patient/medical-chart";
import { AppointmentsSection, CarePlansSection, ChargesSection, DocumentsSection, FamilyHistorySection, FormsSection, IdentificationSection, MembershipsSection, MemosSection, NotesSection, PackagesSection, PatientSidebarSectionSkeleton, PaymentMethodsSection, PinnedMemosSection, SocialHistorySection } from "./patient/sections";
import { GallerySection } from "./patient/sections/gallery-section";
import LabsSection from "./patient/sections/labs-section";
import PastDosesSection from "./patient/sections/past-doses-section";
import { SectionLoadError } from "./patient/sections/section-load-error";
import { SidebarSectionHeader } from "./patient/sidebar-section-header";
import { TodaysAppointments } from "./patient/todays-appointments";
import { useHasUnfinishedTraining } from "@/api/provider/training/training-query";
import { documentQueryOptions } from "@/api/provider/user/patient/document-query";
import { loyaltyPointQueryOptions } from "@/api/provider/user/patient/loyalty-points-query";
import { getAvailableCredit } from "@/api/provider/user/patient/patient-credit";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { AssistantSidebarGroup, askDecodaSidebarDefinition, useAssistantScopeReset, useAssistantSidebarReconciliation } from "@/features/sidebar/assistant-sidebar";
import { type FormSidebarPayload, formSidebarDefinition, reconcileFormSidebarPath } from "@/features/sidebar/form-sidebar";
import { PatientSidebarCommand, patientSidebarDefinition, selectPatientSidebar } from "@/features/sidebar/patient-sidebar";
import { followUpSidebarDefinition, waitlistSidebarDefinition } from "@/features/sidebar/scheduling-sidebar";
import { reconcileSetupSidebarArrival, setupSidebarDefinition } from "@/features/sidebar/setup-sidebar";
import { taskSidebarDefinition } from "@/features/sidebar/task-sidebar";
import { reconcileTrainingSidebarPin, trainingSidebarDefinition } from "@/features/sidebar/training-sidebar";
import { eventQueryOptions, useUpdateEventStatusMutation } from "@/query/check-in/calendar/event-query";
import { measurementQueryOptions } from "@/query/patient/measurements";
import { measurementFieldQueryOptions } from "@/query/settings/measurement-fields";
import { useChartingSidebarTabStore } from "@/stores/charting-sidebar-tab-store";
import { useFollowUpSidebarHostStore } from "@/stores/follow-up-sidebar-host-store";
import { useMemoWindowStore } from "@/stores/memo-window-store";
import { selectSidebarEntries, selectSidebarOpen, useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useWaitlistCancellationStore } from "@/stores/waitlist-cancellation-store";
import { useWaitlistSidebarHostStore } from "@/stores/waitlist-sidebar-host-store";
const AppointmentSkeleton = t0 => {
  const $ = _c(9);
  const {
    selectedEventId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Divider />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = selectedEventId ? "Selected Appointment" : "Upcoming Appointments";
  let t3;
  if ($[1] !== t2) {
    t3 = <h4 className="mb-2 text-caption font-semibold uppercase tracking-wider text-foreground-secondary">{t2}</h4>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="h-4 w-56 animate-pulse rounded bg-muted" />;
    t5 = <div className="mt-2 h-3 w-40 animate-pulse rounded bg-muted" />;
    t6 = <div className="mt-2 h-3 w-36 animate-pulse rounded bg-muted" />;
    t7 = appointmentChecklistSkeleton("mt-2");
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t3) {
    t8 = <>{t1}<div className="mx-4 mb-2">{t3}{t4}{t5}{t6}{t7}</div></>;
    $[7] = t3;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  return t8;
};
function WeightChangeDisplay(t0) {
  const $ = _c(12);
  const {
    value,
    field
  } = t0;
  if (value.recentChange == null || value.trendDirection == null) {
    return null;
  }
  if (value.trendDirection === "neutral") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-foreground-secondary">No change</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const isUp = value.trendDirection.startsWith("up");
  const directionLabel = isUp ? "up" : "down";
  const directionArrow = isUp ? "\u2191" : "\u2193";
  let t1;
  if ($[1] !== field || $[2] !== value.recentChange) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /^[+-]/;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = formatChange(value.recentChange, field).replace(t2, "");
    $[1] = field;
    $[2] = value.recentChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formattedChange = t1;
  let t2;
  if ($[5] !== value.trendDirection) {
    t2 = cx("font-medium", getTrendColor(value.trendDirection));
    $[5] = value.trendDirection;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== directionArrow || $[8] !== directionLabel || $[9] !== formattedChange || $[10] !== t2) {
    t3 = <span className={t2}>{directionArrow} {formattedChange} {directionLabel}</span>;
    $[7] = directionArrow;
    $[8] = directionLabel;
    $[9] = formattedChange;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

// Sidebar action button component for consistent button styling
interface SidebarActionButtonProps {
  label: string;
  href: string;
  newWindow?: boolean;
  className?: string;
  onClick?: () => void;
}
function SidebarActionButton(t0) {
  const $ = _c(11);
  const {
    label,
    href,
    newWindow,
    className,
    onClick
  } = t0;
  const closeSidebar = _temp;
  const {
    isMobile
  } = useIsMobile();
  let t1;
  if ($[0] !== className) {
    t1 = cx("p-1 text-primary", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isMobile ? closeSidebar : undefined;
  let t3;
  if ($[2] !== label || $[3] !== t1 || $[4] !== t2) {
    t3 = <Button variant="ghost" className={t1} onClick={t2}>+ {label}</Button>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== href || $[7] !== newWindow || $[8] !== onClick || $[9] !== t3) {
    t4 = <ProgressBarLink href={href} newWindow={newWindow} onClick={onClick}>{t3}</ProgressBarLink>;
    $[6] = href;
    $[7] = newWindow;
    $[8] = onClick;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp() {
  return useRightSidebarStore.getState().close(patientSidebarDefinition.id);
}
interface PatientSidebarPanelProps {
  hideCloseButton?: boolean;
}
export function PatientSidebarPanel({
  hideCloseButton = false
}: PatientSidebarPanelProps) {
  const isSidebarOpen = useRightSidebarStore(selectSidebarOpen);
  const openMemoWindow = useMemoWindowStore(s => s.openMemoWindow);
  const {
    patientTiny,
    selectedEventId,
    autoExpandSection,
    hideUpcomingEvents
  } = useRightSidebarStore(selectPatientSidebar);
  const closeSidebar = useCallback(() => useRightSidebarStore.getState().close(patientSidebarDefinition.id), []);
  const clearAutoExpandSection = useCallback(() => useRightSidebarStore.getState().open(patientSidebarDefinition, {
    command: PatientSidebarCommand.UPDATE,
    patch: {
      autoExpandSection: null
    }
  }), []);
  const {
    tenant
  } = useTenant();
  const clock = useClinicClock();
  // Patients read the times on the appointment's location clock.
  const locationTimezoneOf = useLocationTimezoneOf();
  const patientId = patientTiny?.id || "";
  const {
    isMobile
  } = useIsMobile();
  const {
    currentUser
  } = useUser();
  const canCreateNotes = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const canViewNotes = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
  const canViewCredits = hasPermission(currentUser, PERMISSIONS.PATIENTS_PROFILES_READ);
  const canViewDocuments = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOCUMENTS_READ);
  const hasLabVendor = useHasAnyLabVendor();
  const canViewLabs = hasLabVendor && hasPermission(currentUser, PERMISSIONS.LABS_READ);
  const pathname = usePathname();
  // In Notes the sidebar is read alongside a note, so it shows the chart
  // unless the user last switched to the profile tab.
  const isCharting = pathname.includes("/scribe");
  const currentNoteId = pathname.match(/\/scribe\/playground\/([^/]+)/)?.[1];
  const {
    tab: chartingTab,
    setTab: setChartingTab
  } = useChartingSidebarTabStore();
  const showFullSidebar = chartingTab === "profile";
  const showChart = isCharting && !showFullSidebar;
  const noteAccordionRef = useRef<HTMLDivElement>(null);
  const memosAccordionRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  // An open section stays closable after its count drops to zero.
  const isEmptyAndClosed = (section: string, count: number | undefined) => !count && !expandedSections.includes(section);

  // Identification section: enabled per-tenant, and only when this patient has
  // an ID document on file.
  const identificationEnabled = showsPatientIdentificationCard(tenant);
  const {
    data: idDocuments = []
  } = useQuery({
    ...documentQueryOptions.idDocuments(patientId),
    enabled: !!patientId && canViewDocuments && identificationEnabled
  });
  const idCount = idDocuments.length;
  const showIdentification = canViewDocuments && identificationEnabled && idCount > 0;

  // Auto-expand the Identification section once per patient when an ID exists,
  // so the thumbnail is visible at a glance. A ref guards against re-expanding
  // after the user manually collapses it.
  const autoExpandedIdPatientRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showIdentification) return;
    if (autoExpandedIdPatientRef.current === patientId) return;
    autoExpandedIdPatientRef.current = patientId;
    setExpandedSections(prev => prev.includes("identification") ? prev : [...prev, "identification"]);
  }, [showIdentification, patientId]);
  const [selectedNoteEventId, setSelectedNoteEventId] = useState<string | null>(null);

  // Auto-expand a section when opened via clinic setup wizard
  useEffect(() => {
    if (autoExpandSection) {
      setExpandedSections([autoExpandSection]);
      clearAutoExpandSection();
    }
  }, [autoExpandSection]);

  // Sidebar snapshot: counts + today's appointments
  const {
    data: sidebarSnapshot
  } = useQuery({
    ...patientQueryOptions.sidebarCounts(patientId),
    enabled: !!patientId
  });

  // Minimal patient detail
  const {
    data: patient,
    isPending: isPatientLoading,
    isRefetching: isPatientRefetching,
    refetch: refetchPatient
  } = useQuery({
    ...patientQueryOptions.detail(patientId),
    enabled: !!patientId
  });

  // Same key checkout and the credits dialog invalidate whenever credit is
  // added, spent or transferred, so the sidebar never shows a stale balance.
  const {
    data: availableCreditData
  } = useQuery({
    queryKey: ["available-credit", patientId],
    queryFn: () => getAvailableCredit(patientId),
    enabled: !!patientId && canViewCredits
  });
  const availableCredit = availableCreditData?.totalAvailable ?? 0;
  const hasLoyaltyPoints = useModule("LOYALTY_POINTS");
  const {
    data: loyaltyPointsData
  } = useQuery({
    ...loyaltyPointQueryOptions.available(patientId ?? ""),
    enabled: !!patientId && hasLoyaltyPoints
  });
  const {
    data: measurementFields = []
  } = useQuery({
    ...measurementFieldQueryOptions.list(),
    enabled: !!patientId
  });
  const weightField = useMemo(() => measurementFields.find(field => field.name.trim().toLowerCase() === "weight" || field.id.trim().toLowerCase() === "weight"), [measurementFields]);
  const {
    data: patientMeasurements
  } = useQuery({
    ...measurementQueryOptions.patient(patientId),
    enabled: !!patientId && !!weightField
  });
  const latestWeightValue = useMemo(() => {
    if (!weightField) return null;
    return patientMeasurements?.values.filter(value => value.fieldId === weightField.id && value.numericValue != null).sort((a, b) => {
      const measurementDiff = toMs(b.measurementDate) - toMs(a.measurementDate);
      if (measurementDiff !== 0) {
        return measurementDiff;
      }
      return toMs(b.createdDate) - toMs(a.createdDate);
    })[0] ?? null;
  }, [patientMeasurements?.values, weightField]);
  const {
    providers = []
  } = useProviders({
    showArchived: true
  });
  const {
    allLocationNameById
  } = useLocations({
    seeAllLocations: true
  });
  const {
    navigateWithProgress
  } = useProgressNavigation();

  // Get preferred provider information
  const preferredProvider = useMemo(() => {
    if (!patient?.preferredProviderId || !providers) return null;
    return providers.find(p => p.id === patient.preferredProviderId);
  }, [patient?.preferredProviderId, providers]);
  const updateEventStatusMutation = useUpdateEventStatusMutation();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  // Where the credits dialog was opened: which patient, on which page. It stays
  // open only while both still match, so a patient switch (e.g. an answered
  // call) or navigation (its "View charge" links may change only ?id=) closes
  // it rather than leaving it over someone else or the new page.
  const currentUrl = `${pathname}?${useSearchParams()?.toString() ?? ""}`;
  const [creditsDialogOpenedAt, setCreditsDialogOpenedAt] = useState<{
    patientId: string;
    url: string;
  } | null>(null);
  const creditsDialogOpen = creditsDialogOpenedAt?.patientId === patientId && creditsDialogOpenedAt.url === currentUrl;
  // Forget a stale anchor during render, so returning to that patient or page
  // later doesn't reopen the dialog.
  if (creditsDialogOpenedAt && !creditsDialogOpen) {
    setCreditsDialogOpenedAt(null);
  }

  // State for cancellation modal
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [eventToCancel, setEventToCancel] = useState<EventDetail | null>(null);
  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const [eventToMarkNoShow, setEventToMarkNoShow] = useState<EventDetail | null>(null);
  const {
    prepare,
    complete,
    dismiss
  } = useWaitlistCancellationStore();
  useEffect(() => {
    if (selectedEventId) {
      queryClient.prefetchQuery({
        ...eventQueryOptions.appointmentChecklist(selectedEventId)
      });
    }
  }, [selectedEventId]);
  const {
    data: selectedEvent
  } = useQuery({
    ...eventQueryOptions.detail(selectedEventId ?? ""),
    enabled: !!selectedEventId && canCreateNotes
  });
  const selectedEventNoteTarget = {
    providerId: currentUser?.id
  };
  const selectedEventNoteTemplateId = getEventNoteTemplateId(selectedEvent, selectedEventNoteTarget);
  const selectedEventNoteTemplateIds = getEventNoteTemplateIds(selectedEvent, selectedEventNoteTarget);

  // Use the full patient data if loaded, otherwise use the patientTiny
  const displayPatient = isPatientLoading || !patient ? patientTiny as PatientChart | null : patient;
  const doseCount = patient?.medications?.length ?? 0;
  const showWeightSummary = tenant?.subDomain === "bhw" && !!weightField && !!latestWeightValue;
  const {
    data: eventsToday,
    isLoading: isEventsTodayLoading
  } = useQuery({
    ...eventQueryOptions.searchPatientEvents(patientId, ""),
    select: data => data?.filter(e => new Date(e.end) >= new Date()),
    enabled: !!patientId && !showChart
  });

  // Check if all appointments are today
  const allAppointmentsAreToday = useMemo(() => {
    if (!eventsToday || eventsToday.length === 0) return false;
    const today = clock.today();
    return eventsToday.every(e_0 => e_0.start && clock.dayOf(e_0.start) === today);
  }, [eventsToday, clock]);
  useEffect(() => {
    if (!isSidebarOpen) {
      setSelectedNoteEventId(null);
    }
  }, [isSidebarOpen]);
  const handleStatusChange = async (event: EventDetail, newStatus: EventStatus) => {
    if (!event || !patientId) return;

    // Special handling for cancellation - open modal instead
    if (newStatus === EventStatus.CANCELLED) {
      setEventToCancel(event);
      if (tenant) prepare(event, tenant.subDomain);
      setIsCancellationModalOpen(true);
      return;
    }
    if (newStatus === EventStatus.NO_SHOW) {
      setEventToMarkNoShow(event);
      setIsNoShowModalOpen(true);
      return;
    }
    updateEventStatusMutation.mutate({
      eventId: event.id,
      newStatus,
      patientId
    });
  };
  const handleCancellationConfirm = async (cancellationReason: string, sendSms: boolean, cancellationMessage?: string, recurrenceModificationMode?: RecurrenceModificationMode, chargeCancellationFee?: boolean) => {
    if (!eventToCancel || !patientId) return;
    const cancelledEvent = eventToCancel;
    const tenantAtCancelTime = tenant;
    try {
      const result = await updateEventStatusMutation.mutateAsync({
        eventId: cancelledEvent.id,
        newStatus: EventStatus.CANCELLED,
        patientId,
        cancellationReason,
        sendSms,
        cancellationMessage,
        recurrenceModificationMode,
        chargeCancellationFee
      });
      // mutateAsync continues even when an optimistic calendar update unmounts
      // this panel; per-call onSuccess callbacks do not.
      if (result && tenantAtCancelTime) {
        complete(cancelledEvent, tenantAtCancelTime.subDomain);
      } else {
        dismiss();
      }
      setEventToCancel(null);
      setIsCancellationModalOpen(false);
    } catch {
      dismiss();
    }
  };
  const handleNoShowConfirm = async (chargeNoShowFee?: boolean, sendSms_0?: boolean) => {
    if (!eventToMarkNoShow || !patientId) return;
    updateEventStatusMutation.mutate({
      eventId: eventToMarkNoShow.id,
      newStatus: EventStatus.NO_SHOW,
      patientId,
      chargeNoShowFee,
      sendSms: sendSms_0
    });
  };

  // Undefined until the location's timezone is known. The cancellation modal
  // waits for it, so the default SMS is never written without the
  // appointment's date and time and then swapped in under someone typing.
  const cancelTimeZone = eventToCancel ? locationTimezoneOf(eventToCancel.location?.id) : undefined;
  const defaultCancellationMessage = useMemo(() => {
    if (!eventToCancel || !tenant || !cancelTimeZone) return "";
    return generateAppointmentCancellationMessage(eventToCancel, tenant.name, cancelTimeZone);
  }, [eventToCancel, tenant, cancelTimeZone]);
  const defaultCancellationMessageFromTemplate = useMemo(() => !!(eventToCancel && getEventCancellationTemplate(eventToCancel)), [eventToCancel]);
  const handleViewNote = useCallback((eventId?: string, noteId?: string) => {
    if (noteId) {
      navigateWithProgress(`/scribe/playground/${noteId}`);
      return;
    }

    // Fall back to highlighting the note in the sidebar if we can't resolve
    // its id yet (e.g. notes list still loading).
    if (eventId !== undefined) {
      setSelectedNoteEventId(eventId);
    }
    if (!expandedSections.includes("notes")) {
      setExpandedSections([...expandedSections, "notes"]);
    }
    noteAccordionRef.current?.scrollIntoView({
      behavior: "smooth"
    });
  }, [expandedSections, navigateWithProgress]);
  const handleOpenMemosSection = useCallback(() => {
    if (!expandedSections.includes("memos")) {
      setExpandedSections([...expandedSections, "memos"]);
    }
    // Scroll to memos section - use longer timeout to ensure accordion is expanded
    setTimeout(() => {
      memosAccordionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 200);
  }, [expandedSections]);
  return <ScrollShadow.Root>
      <ScrollShadow.Viewport className="h-full overflow-y-auto overflow-x-hidden">
        <div className="absolute right-2 top-2 z-10 flex items-center">
          {(sidebarSnapshot?.memosCount ?? 0) > 0 && <Button onClick={handleOpenMemosSection} variant="ghost" size="icon" icon={<FaRegNoteSticky className="size-3.5" />} tooltip="Click to view memos">
              <span className="text-caption font-medium">
                {sidebarSnapshot?.memosCount ?? 0}
              </span>
            </Button>}
          {!hideCloseButton && <Button onClick={closeSidebar} variant="ghost" size="icon" icon={<RiCloseLine className="size-3.5" />} />}
        </div>
        {displayPatient ? <>
            <div className="mx-4 mt-4">
              <div className="flex items-center">
                <ProfilePictureUpload patient={displayPatient} />
                <div className="ml-2 min-w-0 flex-1 overflow-hidden">
                  <PatientDisplay nameClassName="text-h2" patient={displayPatient} useLinks={false} />
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                  <PatientIdentifier patient={displayPatient} truncate={false} />
                </div>
                <div className="flex items-center gap-1 text-caption text-foreground-secondary">
                  <span>Patient Since</span>
                  <span className="text-foreground-secondary">
                    {formatDate(displayPatient.createdDate)}
                  </span>
                </div>
                {displayPatient.dateOfBirth && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Date of Birth:</span>
                    <span className="text-foreground-secondary">
                      {formatDate(displayPatient.dateOfBirth)}
                    </span>
                    <span className="text-foreground-secondary">
                      ({calculateAge(displayPatient.dateOfBirth)} years old)
                    </span>
                  </div>}
                {displayPatient.gender && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Gender:</span>
                    <span className="text-foreground-secondary">
                      {formatToHumanReadable(displayPatient.gender)}
                    </span>
                  </div>}
                {showWeightSummary && weightField && latestWeightValue && <>
                    <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                      <span>Weight:</span>
                      <span className="font-medium text-foreground">
                        {formatMeasurementValue(latestWeightValue, weightField)}
                      </span>
                      <WeightChangeDisplay value={latestWeightValue} field={weightField} />
                    </div>
                    <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                      <span>Recorded:</span>
                      <span>
                        {formatDate(latestWeightValue.measurementDate)}
                      </span>
                    </div>
                  </>}
                {displayPatient.pronouns && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Pronouns:</span>
                    <span className="text-foreground-secondary">
                      {displayPatient.pronouns}
                    </span>
                  </div>}
                {displayPatient.externalId && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <IdDisplay id={displayPatient.externalId} label="External ID" truncate={false} />
                  </div>}
                {displayPatient.leadSource && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Source:</span>
                    <span className="text-foreground-secondary">
                      {formatToHumanReadable(displayPatient.leadSource)}
                    </span>
                  </div>}
                {showsPhoneNumber(displayPatient) && <div className="flex items-center gap-1 text-caption">
                    <span className="text-foreground-secondary">Phone:</span>
                    <ClickablePhoneNumber patient={displayPatient} className="text-caption" onClick={() => closeSidebar()} />
                  </div>}
                {showsEmail(displayPatient) && <div className="flex items-center gap-1 text-caption">
                    <span className="text-foreground-secondary">Email:</span>
                    {displayPatient.email ? <ClickableEmail email={displayPatient.email} patientId={displayPatient.id} /> : <HiddenContact className="text-caption" />}
                  </div>}
                {(displayPatient.address || displayPatient.city || displayPatient.state || displayPatient.zipCode) && <div className="flex flex-row items-start gap-1 text-caption">
                    <span className="text-foreground-secondary">Address:</span>
                    {formatAddress(displayPatient.address, displayPatient.city, displayPatient.state, displayPatient.zipCode, displayPatient.addressLineTwo, "text-foreground-secondary text-caption")}
                  </div>}
                {preferredProvider && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Preferred Provider:</span>
                    <span className="font-medium text-foreground">
                      {formatStaffName(preferredProvider)}
                    </span>
                  </div>}
                {displayPatient.primaryLocationId && allLocationNameById[displayPatient.primaryLocationId] && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                      <span>Location:</span>
                      <span>
                        {allLocationNameById[displayPatient.primaryLocationId]}
                      </span>
                    </div>}
                {hasLoyaltyPoints && loyaltyPointsData && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Loyalty Points:</span>
                    <span className="font-bold text-foreground">
                      {loyaltyPointsData.totalAvailable.toLocaleString()}
                    </span>
                  </div>}
                {canViewCredits && availableCredit > 0 && <div className="flex items-center gap-2 text-caption text-foreground-secondary">
                    <span>Credits:</span>
                    <button type="button" onClick={() => setCreditsDialogOpenedAt({
                patientId,
                url: currentUrl
              })} aria-label={`Manage credits, ${formatCurrency(availableCredit)} available`} className={cx(clickableText, "font-bold")}>
                      {formatCurrency(availableCredit)}
                    </button>
                  </div>}
                {displayPatient.surchargeDisabled && <div className="flex items-center gap-1 text-caption">
                    <span className="text-foreground-secondary">
                      Surcharges:
                    </span>
                    <Badge variant="warning">Disabled</Badge>
                  </div>}
              </div>
              {/* Mounted only while open, so closing it for any reason (patient
                  switch, navigation, lost access) also drops its delete and
                  transfer confirmations and any half-filled form. */}
              {canViewCredits && creditsDialogOpen && <PatientCreditsDialog patientId={displayPatient.id} open onOpenChange={open => setCreditsDialogOpenedAt(open ? {
            patientId,
            url: currentUrl
          } : null)} />}
              <div className="my-4">
                <ProgressBarLink href={`/patient/${displayPatient.id}`} onClick={() => closeSidebar()} className={clickableText}>
                  See {formatName(displayPatient.firstName, undefined)}
                  &apos;s full profile
                </ProgressBarLink>
              </div>
              {isCharting && <Tabs value={chartingTab} onValueChange={value_0 => setChartingTab(value_0 === "profile" ? "profile" : "chart")} className="mb-3">
                  <TabsList variant="solid" className="flex w-full">
                    <TabsTrigger value="chart" className="flex-1">
                      Chart
                    </TabsTrigger>
                    <TabsTrigger value="profile" className="flex-1">
                      Profile
                    </TabsTrigger>
                  </TabsList>
                </Tabs>}
            </div>
          </> : null}

        {displayPatient ? <PinnedMemosSection patient={displayPatient} /> : null}

        {hideUpcomingEvents ? null : selectedEventId ? <>
            <Divider />
            <div className="mx-4 mb-2">
              <h4 className="mb-2 text-caption font-semibold uppercase tracking-wider text-foreground-secondary">
                Selected Appointment
              </h4>
              <TodaysAppointments eventId={selectedEventId} handleViewNote={handleViewNote}>
                <TodaysAppointments.Checklist />
              </TodaysAppointments>
            </div>
          </> : showChart ? null : isEventsTodayLoading ? <AppointmentSkeleton selectedEventId={selectedEventId} /> : eventsToday && eventsToday.length > 0 ? <>
            <Divider />
            <div className="mx-4 mb-2">
              <h4 className="mb-2 text-caption font-semibold uppercase tracking-wider text-foreground-secondary">
                {allAppointmentsAreToday ? `Today's Appointment${eventsToday.length > 1 ? "s" : ""}` : `Upcoming Appointment${eventsToday.length > 1 ? "s" : ""}`}
              </h4>
              {eventsToday.map((event_0: EventDetail) => <TodaysAppointments key={event_0.id} eventId={event_0.id} event={event_0} handleViewNote={handleViewNote}>
                  {shouldShowSidebarChecklist(event_0, eventsToday) ? <TodaysAppointments.Checklist /> : null}
                </TodaysAppointments>)}
            </div>
          </> : null}

        {displayPatient ? <>
            <Divider className="mb-0" />
            {showChart ? patient ? <MedicalChart patient={patient} currentNoteId={currentNoteId} /> : <div className="px-4 pt-4">
                  {/* The detail fetcher returns null on failure, which the
                      select maps to undefined once the read has settled. */}
                  {isPatientLoading ? <PatientSidebarSectionSkeleton /> : <SectionLoadError label="the patient's chart" onRetry={() => refetchPatient()} isRetrying={isPatientRefetching} />}
                </div> : <Accordion type="multiple" className="mt-2 px-4 pb-6" onValueChange={value_1 => {
          if (value_1.includes("notes") && !expandedSections.includes("notes")) {
            noteAccordionRef.current?.scrollIntoView({
              behavior: "smooth"
            });
          }
          setExpandedSections(value_1);
        }} value={expandedSections}>
                {/* Identification Section */}
                {showIdentification && <AccordionItem value="identification" className="py-1">
                    <SidebarSectionHeader title="Identification" icon={FaRegAddressCard} />
                    <AccordionContent>
                      <IdentificationSection patientId={displayPatient.id} />
                    </AccordionContent>
                  </AccordionItem>}
                {/* Memos Section */}
                <AccordionItem value="memos" className="py-1" ref={memosAccordionRef}>
                  <SidebarSectionHeader title="Memos" icon={FaRegNoteSticky} count={sidebarSnapshot?.memosCount ?? 0} disabled={isEmptyAndClosed("memos", sidebarSnapshot?.memosCount)} action={<Button variant="ghost" className="p-1 text-primary" onClick={() => displayPatient && openMemoWindow({
              patient: displayPatient
            })}>
                        + Create
                      </Button>} />
                  <AccordionContent>
                    <MemosSection patient={displayPatient} />
                  </AccordionContent>
                </AccordionItem>
                {/* Memberships Section */}
                <AccordionItem value="memberships" className="py-1">
                  <SidebarSectionHeader title="Memberships" icon={FiUsers} count={sidebarSnapshot?.membershipsCount ?? 0} disabled={isEmptyAndClosed("memberships", sidebarSnapshot?.membershipsCount)} action={<SidebarActionButton label="Add" href={`/patient/${displayPatient.id}?tab=memberships`} />} />
                  <AccordionContent>
                    <MembershipsSection patientId={displayPatient.id} memberships={patient?.memberships ?? []} isLoading={isPatientLoading} />
                  </AccordionContent>
                </AccordionItem>
                {/* Packages Section */}
                <AccordionItem value="packages" className="py-1">
                  <SidebarSectionHeader title="Packages" icon={FiPackage} count={sidebarSnapshot?.packagesCount ?? 0} disabled={isEmptyAndClosed("packages", sidebarSnapshot?.packagesCount)} action={<SidebarActionButton label="Sell" href={`/patient/${displayPatient.id}?tab=packages`} />} />
                  <AccordionContent>
                    <PackagesSection patientId={displayPatient.id} />
                  </AccordionContent>
                </AccordionItem>
                {/* Appointments Section - Moved up under memos */}
                <AccordionItem value="appointments" className="py-1">
                  <SidebarSectionHeader title="Appointments" icon={FaRegCalendarAlt} count={sidebarSnapshot?.appointmentsCount ?? 0} disabled={isEmptyAndClosed("appointments", sidebarSnapshot?.appointmentsCount)} action={<SidebarActionButton label="Schedule" href={`/check-in/appointment-form/new?patientId=${displayPatient.id}`} />} />
                  <AccordionContent>
                    <AppointmentsSection patientId={displayPatient.id} onStatusChange={handleStatusChange} handleViewNote={handleViewNote} />
                  </AccordionContent>
                </AccordionItem>
                {/* Care Plans Section */}
                <AccordionItem value="care-plans" className="py-1">
                  <SidebarSectionHeader title="Care Plans" icon={FiClipboard} count={sidebarSnapshot?.carePlansCount ?? 0} disabled={isEmptyAndClosed("care-plans", sidebarSnapshot?.carePlansCount)} action={<SidebarActionButton label="Assign" href={`/patient/${displayPatient.id}/care-plan/new`} />} />
                  <AccordionContent>
                    <CarePlansSection patientId={displayPatient.id} />
                  </AccordionContent>
                </AccordionItem>
                {/* Social History Section */}
                {tenant?.showSocialHistoryInSidebar && <AccordionItem value="social-history" className="py-1">
                    {/* Always expandable so an empty section still shows "None recorded". */}
                    <SidebarSectionHeader title="Social History" icon={FaHeartbeat} count={getSocialHistory(displayPatient).length} />
                    <AccordionContent>
                      <SocialHistorySection patient={displayPatient} />
                    </AccordionContent>
                  </AccordionItem>}
                {/* Family History Section */}
                {tenant?.showFamilyHistoryInSidebar && <AccordionItem value="family-history" className="py-1">
                    {/* Always expandable so an empty section still shows "None recorded". */}
                    <SidebarSectionHeader title="Family History" icon={FaUsers} count={getFamilyHistory(displayPatient).length} />
                    <AccordionContent>
                      <FamilyHistorySection patient={displayPatient} />
                    </AccordionContent>
                  </AccordionItem>}
                {/* Charges Section */}
                <AccordionItem value="charges" className="py-1">
                  <SidebarSectionHeader title="Charges" icon={FaRegMoneyBillAlt} count={sidebarSnapshot?.chargesCount ?? 0} disabled={isEmptyAndClosed("charges", sidebarSnapshot?.chargesCount)} action={<SidebarActionButton label="Create" href={`/billing/check-out${patient?.id ? `?patientId=${patient?.id}` : ""}`} />} />
                  <AccordionContent>
                    <ChargesSection patientId={displayPatient.id} />
                  </AccordionContent>
                </AccordionItem>
                {/* Payment Methods Section */}
                <AccordionItem value="payment-methods" className="py-1">
                  <SidebarSectionHeader title="Payment Methods" icon={FaRegCreditCard} count={sidebarSnapshot?.paymentMethodsCount ?? 0} disabled={isEmptyAndClosed("payment-methods", sidebarSnapshot?.paymentMethodsCount)} action={<AddPaymentMethodModal patientId={displayPatient.id} onSuccess={_paymentMethod => {}}>
                        <Button variant="ghost" className="p-1 text-primary">
                          + Add
                        </Button>
                      </AddPaymentMethodModal>} />
                  <AccordionContent>
                    <PaymentMethodsSection patientId={displayPatient.id} />
                  </AccordionContent>
                </AccordionItem>
                {/* Forms Section */}
                <AccordionItem value="forms" className="py-1">
                  <SidebarSectionHeader title="Forms" icon={FaRegFileAlt} count={sidebarSnapshot?.formsCount ?? 0} disabled={isEmptyAndClosed("forms", sidebarSnapshot?.formsCount)} action={patient && (patient.phoneNumber || patient.email ? <SendFormsDialog patient={patient}>
                          <Button variant="ghost" className="p-1 text-primary">
                            + Send
                          </Button>
                        </SendFormsDialog> : <Tooltip content="Patient needs a phone number or email address to receive forms">
                          <div>
                            <Button variant="ghost" className="p-1 text-foreground-muted" disabled>
                              + Send
                            </Button>
                          </div>
                        </Tooltip>)} />
                  <AccordionContent>
                    <FormsSection patient={displayPatient} />
                  </AccordionContent>
                </AccordionItem>
                {/* Doses Section */}
                <AccordionItem value="doses" className="py-1">
                  <SidebarSectionHeader title="Doses" icon={FaSyringe} count={patient ? doseCount : undefined} disabled={isEmptyAndClosed("doses", doseCount)} action={<AddDoseWrapper patientId={displayPatient.id} patient={patient} onSubmit={() => {}} align="end">
                        <Button variant="ghost" className="p-1 text-primary">
                          + Add
                        </Button>
                      </AddDoseWrapper>} />
                  <AccordionContent>
                    {patient && <PastDosesSection patient={patient} />}
                  </AccordionContent>
                </AccordionItem>
                {/* Notes Section */}
                <AccordionItem value="notes" className="py-1" ref={noteAccordionRef}>
                  <SidebarSectionHeader title="Medical Notes" icon={FaRegStickyNote} count={canViewNotes ? sidebarSnapshot?.notesCount ?? 0 : undefined}
            // Stays enabled without view permission so the stub can show.
            disabled={canViewNotes && isEmptyAndClosed("notes", sidebarSnapshot?.notesCount)} action={canCreateNotes ? <CreateNoteWrapper patient={displayPatient} onCreateNote={isMobile ? closeSidebar : undefined} defaultNoteTemplateId={selectedEventNoteTemplateId} suggestedNoteTemplateIds={selectedEventNoteTemplateIds} promptAppointment={selectedEvent ? {
              id: selectedEvent.id,
              locationId: selectedEvent.location?.id
            } : undefined}>
                          <Button variant="ghost" className="p-1 text-primary">
                            + Create
                          </Button>
                        </CreateNoteWrapper> : <Tooltip content="You don't have permission to create notes">
                          <div>
                            <Button variant="ghost" className="p-1 text-foreground-muted" disabled>
                              + Create
                            </Button>
                          </div>
                        </Tooltip>} />
                  <AccordionContent>
                    <NotesSection patientId={displayPatient.id} enabled={expandedSections.includes("notes")} selectedEventId={selectedNoteEventId ?? selectedEventId ?? undefined} />
                  </AccordionContent>
                </AccordionItem>
                {/* Labs Section */}
                {canViewLabs && <AccordionItem value="labs" className="py-1">
                    <SidebarSectionHeader title="Labs" icon={TbTestPipe} />
                    <AccordionContent>
                      <LabsSection patientId={displayPatient.id} />
                    </AccordionContent>
                  </AccordionItem>}
                {/* Documents Section */}
                {canViewDocuments && <AccordionItem value="documents" className="py-1">
                    <SidebarSectionHeader title="Documents" icon={FaRegFilePdf} count={sidebarSnapshot?.documentsCount ?? 0} disabled={isEmptyAndClosed("documents", sidebarSnapshot?.documentsCount)} action={<Button variant="ghost" className="p-1 text-primary" onClick={() => setUploadDialogOpen(true)}>
                          + Upload
                        </Button>} />
                    <AccordionContent>
                      <DocumentsSection patientId={displayPatient.id} />
                    </AccordionContent>
                  </AccordionItem>}
                {/* Attachment History Section */}
                <AccordionItem value="attachment-history" className="py-1">
                  <SidebarSectionHeader title="Gallery" icon={FaRegImages} count={sidebarSnapshot?.attachmentsCount ?? 0} disabled={isEmptyAndClosed("attachment-history", sidebarSnapshot?.attachmentsCount)} action={<Button variant="ghost" className="p-1 text-primary" aria-label="Add to Gallery" onClick={() => setGalleryDialogOpen(true)}>
                        + Add
                      </Button>} />
                  <AccordionContent>
                    <GallerySection patientId={displayPatient.id} size="sm" />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>}
            <Dialog open={galleryDialogOpen} onOpenChange={setGalleryDialogOpen}>
              <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add to Gallery</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <GallerySection patientId={displayPatient.id} size="lg" />
                </DialogBody>
              </DialogContent>
            </Dialog>
            {/* Document Upload Dialog */}
            {canViewDocuments && patient && <DocumentUpload open={uploadDialogOpen} onOpenChange={setUploadDialogOpen} patientId={patient.id} />}
            {/* Appointment Cancellation Modal */}
            <AppointmentCancellationModal isOpen={isCancellationModalOpen && !!cancelTimeZone} setIsOpen={open_0 => {
          setIsCancellationModalOpen(open_0);
          if (!open_0) dismiss();
        }} onConfirm={handleCancellationConfirm} event={eventToCancel ?? undefined} isLoading={updateEventStatusMutation.isPending} defaultCancellationMessage={defaultCancellationMessage} defaultCancellationMessageFromTemplate={defaultCancellationMessageFromTemplate} showCancellationFeeToggle autoChargeCardOnFile={tenant?.autoChargeCancellationFees ?? false} />
            <AppointmentNoShowConfirmationModal isOpen={isNoShowModalOpen} setIsOpen={setIsNoShowModalOpen} onConfirm={handleNoShowConfirm} event={eventToMarkNoShow ?? undefined} isLoading={updateEventStatusMutation.isPending} showNoShowFeeToggle autoChargeCardOnFile={tenant?.autoChargeNoShowFees ?? false} />
          </> : null}
      </ScrollShadow.Viewport>
      <ScrollShadow.Top />
      <ScrollShadow.Bottom />
    </ScrollShadow.Root>;
}

/**
 * The calendar's scheduling panel: the Follow-Up panel for clinics with the
 * FOLLOW_UP module, the waitlist otherwise. Either one portals its content in.
 */
function WaitlistSidebarPanel(t0) {
  const $ = _c(5);
  const {
    hideCloseButton: t1,
    followUp: t2
  } = t0;
  const hideCloseButton = t1 === undefined ? false : t1;
  const followUp = t2 === undefined ? false : t2;
  const setWaitlistContainer = useWaitlistSidebarHostStore(_temp12);
  const setFollowUpContainer = useFollowUpSidebarHostStore(_temp13);
  const setContainer = followUp ? setFollowUpContainer : setWaitlistContainer;
  let t3;
  if ($[0] !== hideCloseButton || $[1] !== setContainer) {
    t3 = container => setContainer(container, hideCloseButton);
    $[0] = hideCloseButton;
    $[1] = setContainer;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const panelRef = t3;
  let t4;
  if ($[3] !== panelRef) {
    t4 = <div ref={panelRef} className="h-full min-h-0 w-full" />;
    $[3] = panelRef;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function _temp13(state_0) {
  return state_0.setContainer;
}
function _temp12(state) {
  return state.setContainer;
}
const FormSubmissionsPanel = dynamic(() => import("@/components/form/submissions-drawer").then(module => module.SubmissionsPanel));

// Renderers are attached here, not imported by strategy modules. The registry is
// the single source of both header metadata and panel content.
const formPanelDefinition = {
  ...formSidebarDefinition,
  render: (entry: SidebarEntry<FormSidebarPayload>) => <FormSubmissionsPanel key={entry.revision} {...entry.payload} />
};
const patientPanelDefinition = {
  ...patientSidebarDefinition,
  render: () => <PatientSidebarPanel hideCloseButton />
};
const taskPanelDefinition = {
  ...taskSidebarDefinition,
  render: () => <TaskSidebarPanel onClose={() => useRightSidebarStore.getState().close(taskSidebarDefinition.id)} />
};
const setupPanelDefinition = {
  ...setupSidebarDefinition,
  render: () => <SetupSidebarPanel />
};
const trainingPanelDefinition = {
  ...trainingSidebarDefinition,
  render: () => <TrainingSidebarPanel />
};
const followUpPanelDefinition = {
  ...followUpSidebarDefinition,
  render: () => <WaitlistSidebarPanel hideCloseButton followUp />
};
const waitlistPanelDefinition = {
  ...waitlistSidebarDefinition,
  render: () => <WaitlistSidebarPanel hideCloseButton />
};
function useRootSidebarRegistrations(enabled) {
  const followUpMounted = useFollowUpSidebarHostStore(_temp14);
  const waitlistMounted = useWaitlistSidebarHostStore(_temp15);
  useSidebarRegistration(patientPanelDefinition, enabled);
  useSidebarRegistration(taskPanelDefinition, enabled);
  useSidebarRegistration(formPanelDefinition, enabled);
  useSidebarRegistration(setupPanelDefinition, enabled);
  useSidebarRegistration(trainingPanelDefinition, enabled);
  useSidebarRegistration(followUpPanelDefinition, enabled && followUpMounted);
  useSidebarRegistration(waitlistPanelDefinition, enabled && waitlistMounted);
  useSidebarRegistration(askDecodaSidebarDefinition, enabled);
}
function _temp15(state_0) {
  return state_0.hostMounted;
}
function _temp14(state) {
  return state.hostMounted;
}
export function RightSidebarContent() {
  const $ = _c(9);
  const state = useRightSidebarStore();
  const entries = selectSidebarEntries(state);
  const trainingPinned = useHasUnfinishedTraining();
  const selectedDefinition = state.activeKey ? state.registrations[state.activeKey]?.definition : undefined;
  const isAssistantActive = selectedDefinition?.group === AssistantSidebarGroup.ASSISTANT;
  const selectedLabel = selectedDefinition?.label;
  let t0;
  let t1;
  if ($[0] !== isAssistantActive || $[1] !== selectedLabel) {
    t0 = () => {
      if (!isAssistantActive) {
        return;
      }
      posthog.startSessionRecording({
        sampling: true
      });
      posthog.capture("ai_sidebar_opened", {
        aiTab: selectedLabel
      });
    };
    t1 = [isAssistantActive, selectedLabel];
    $[0] = isAssistantActive;
    $[1] = selectedLabel;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  if (!entries.length) {
    return null;
  }
  let t2;
  if ($[4] !== state || $[5] !== trainingPinned) {
    t2 = entry => ({
      id: entry.key,
      label: state.registrations[entry.key].definition.label,
      onClose: entry.key === trainingSidebarDefinition.id && trainingPinned ? undefined : () => state.close(entry.key)
    });
    $[4] = state;
    $[5] = trainingPinned;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const headerTabs = entries.map(t2);
  let t3;
  if ($[7] !== state.registrations) {
    t3 = entry_0 => <TabsContent key={entry_0.key} value={entry_0.key} className="mt-0 min-h-0 flex-1 flex-col overflow-hidden data-[state=active]:flex">{state.registrations[entry_0.key].definition.render?.(entry_0)}</TabsContent>;
    $[7] = state.registrations;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return <Tabs value={state.activeKey ?? undefined} onValueChange={state.select} className="flex h-full w-full flex-col"><SidebarHeader tabs={headerTabs} />{entries.map(t3)}</Tabs>;
}
function RightSidebar() {
  const $ = _c(52);
  useAssistantScopeReset();
  useAssistantSidebarReconciliation();
  const scopeReady = useSidebarEnvironment();
  useRootSidebarRegistrations(scopeReady);
  useTaskSidebarPatientSync();
  const state = useRightSidebarStore();
  const {
    width,
    setWidth,
    closeAll
  } = state;
  let t0;
  if ($[0] !== scopeReady || $[1] !== state) {
    t0 = scopeReady && selectSidebarOpen(state);
    $[0] = scopeReady;
    $[1] = state;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isOpen = t0;
  const pathname = usePathname();
  let t1;
  let t2;
  if ($[3] !== pathname) {
    t1 = () => {
      reconcileSetupSidebarArrival(pathname);
      reconcileFormSidebarPath(pathname);
    };
    t2 = [pathname];
    $[3] = pathname;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const trainingPinned = useHasUnfinishedTraining();
  let t3;
  if ($[6] !== state) {
    const openKeys = selectSidebarEntries(state).map(_temp16);
    t3 = openKeys.join(",");
    $[6] = state;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const openKeysSignature = t3;
  let t4;
  if ($[8] !== pathname) {
    t4 = pathname.split("/").slice(2).join("/");
    $[8] = pathname;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onTrainingPage = t4 === "training";
  let t5;
  let t6;
  if ($[10] !== onTrainingPage || $[11] !== openKeysSignature || $[12] !== scopeReady || $[13] !== trainingPinned) {
    t5 = () => {
      if (!scopeReady || onTrainingPage) {
        return;
      }
      reconcileTrainingSidebarPin(openKeysSignature.split(",").filter(Boolean), trainingPinned);
    };
    t6 = [scopeReady, onTrainingPage, openKeysSignature, trainingPinned];
    $[10] = onTrainingPage;
    $[11] = openKeysSignature;
    $[12] = scopeReady;
    $[13] = trainingPinned;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  const {
    isMobile
  } = useIsMobile();
  const isDraggingRef = useRef(false);
  const sidebarRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const drawerTitle = (state.activeKey ? state.registrations[state.activeKey]?.definition.label : undefined) ?? "Sidebar";
  let t7;
  if ($[16] !== closeAll) {
    t7 = open => {
      if (!open) {
        closeAll();
      }
    };
    $[16] = closeAll;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const onDrawerOpenChange = t7;
  let t8;
  if ($[18] !== setWidth) {
    t8 = e => {
      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);
      const onMouseMove = moveEvent => {
        if (!isDraggingRef.current || !sidebarRef.current) {
          return;
        }
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = sidebarRect.right - moveEvent.clientX;
        setWidth(newWidth);
      };
      const onMouseUp = () => {
        isDraggingRef.current = false;
        setIsDragging(false);
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };
    $[18] = setWidth;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleMouseDown = t8;
  if (!scopeReady) {
    return null;
  }
  if (isMobile) {
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <CancellationWaitlistDialog />;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== drawerTitle) {
      t10 = <DrawerHeader className="sr-only" showCloseButton={false}><DrawerTitle>{drawerTitle}</DrawerTitle></DrawerHeader>;
      $[21] = drawerTitle;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <DrawerBody className="m-0 bg-background/80 p-0"><RightSidebarContent /></DrawerBody>;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t10) {
      t12 = <DrawerContent className="m-0 p-0">{t10}{t11}</DrawerContent>;
      $[24] = t10;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== isOpen || $[27] !== onDrawerOpenChange || $[28] !== t12) {
      t13 = <>{t9}<Drawer open={isOpen} onOpenChange={onDrawerOpenChange}>{t12}</Drawer></>;
      $[26] = isOpen;
      $[27] = onDrawerOpenChange;
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    return t13;
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CancellationWaitlistDialog />;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const t10 = !isDragging && "transition-all duration-200 ease-in-out";
  const t11 = isOpen ? "scale-100 opacity-100" : "hidden scale-95 opacity-0 md:block";
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = cx("relative my-2 flex flex-shrink-0 rounded-lg border border-border bg-background/80 backdrop-blur-sm", "min-w-0 transform-gpu", "shadow-md", t10, t11);
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = isOpen ? "0.5rem" : "0rem";
  const t14 = isOpen ? `${width}px` : "0rem";
  const t15 = `${width}px`;
  let t16;
  if ($[34] !== t13 || $[35] !== t14 || $[36] !== t15) {
    t16 = {
      marginRight: t13,
      width: t14,
      willChange: "opacity, transform, width",
      "--right-sidebar-width": t15
    };
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = t16 as React.CSSProperties;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cx("absolute -left-1 top-0 z-10 h-full w-2 cursor-col-resize", "group flex items-center justify-center");
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = isDragging ? "bg-foreground-secondary" : "bg-transparent group-hover:bg-foreground-tertiary";
  let t20;
  if ($[39] !== t19) {
    t20 = cx("h-8 w-1 rounded-full transition-colors", t19);
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20) {
    t21 = <div className={t20} />;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== handleMouseDown || $[44] !== t21) {
    t22 = <div onMouseDown={handleMouseDown} className={t18}>{t21}</div>;
    $[43] = handleMouseDown;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="pointer-events-none absolute inset-0 rounded-lg" />;
    t24 = <RightSidebarContent />;
    $[46] = t23;
    $[47] = t24;
  } else {
    t23 = $[46];
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t12 || $[49] !== t17 || $[50] !== t22) {
    t25 = <>{t9}<div ref={sidebarRef} key="sidebar" data-right-sidebar="" className={t12} style={t17}>{t22}{t23}{t24}</div></>;
    $[48] = t12;
    $[49] = t17;
    $[50] = t22;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  return t25;
}
function _temp16(entry) {
  return entry.key;
}
export default RightSidebar;